import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { AppState } from 'src/app/store/states/app.state';
import { isUserAuthenticated } from 'src/app/store/selectors/user.selector';
import { catchError, map, switchMap } from 'rxjs/operators';
import { setCurrentUser } from 'src/app/store/actions/user.action';
import { UserProfileService } from 'src/app/api/services';

@Injectable({
  providedIn: 'root'
}) export class AuthGuard  {
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private userProfileService: UserProfileService
  ) { }

  static canActivateFn: CanActivateFn  = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const  authGuard = inject(AuthGuard);
    return authGuard.checkUserAuth(state.url);
  }

  static  canMatchFn = (route: Route, segments: UrlSegment[]) => {
    const  authGuard = inject(AuthGuard);
    const url = segments.reduce((path, currentSegment) => {
      return `${path}/${currentSegment.path}`;
    }, '');
    return authGuard.checkUserAuth(url);
  }

  public checkUserAuth(returnUrl: string): Observable<boolean> {
    return this.store.select(isUserAuthenticated).pipe(
      switchMap(isAuth => {
        if (isAuth) {
          return of(true);
        }

        return this.userProfileService.getCurrentUser()
          .pipe(
            map((user) => {
              if (user) {
                this.store.dispatch(setCurrentUser({ user }));
                return true;
              }
              
              return this.redirectToLogin(returnUrl);
            }),
            catchError(_ => of(this.redirectToLogin(returnUrl)))
          )
      })
    );
  }

  private redirectToLogin(returnUrl: string) {
    this.router.navigate(['/login'], {
      queryParams: { returnUrl }
    });

    return true;
  }
}
